import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  billingScenariosService,
  CreateBillingScenarioInput,
  UpdateBillingScenarioInput,
} from "../../services/admin/billingscenarios.service";

export function useBillingScenarios() {
  return useQuery({
    queryKey: ["billing-scenarios"],
    queryFn: billingScenariosService.getAll,
  });
}

export function useBillingScenariosAdmin() {
  return useQuery({
    queryKey: ["admin", "billing-scenarios"],
    queryFn: billingScenariosService.getAll,
  });
}

export function useBillingScenario(id?: string) {
  return useQuery({
    queryKey: ["admin", "billing-scenarios", id],
    queryFn: () => billingScenariosService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateBillingScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateBillingScenarioInput) =>
      billingScenariosService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "billing-scenarios"],
      });
      queryClient.invalidateQueries({ queryKey: ["billing-scenarios"] });
      notifications.show({
        title: "Success",
        message: "Billing Scenario created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create billing scenario",
        color: "red",
      });
    },
  });
}

export function useUpdateBillingScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateBillingScenarioInput) =>
      billingScenariosService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "billing-scenarios"],
      });
      queryClient.invalidateQueries({ queryKey: ["billing-scenarios"] });
      notifications.show({
        title: "Success",
        message: "Billing Scenario updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update billing scenario",
        color: "red",
      });
    },
  });
}

export function useDeleteBillingScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billingScenariosService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "billing-scenarios"],
      });
      queryClient.invalidateQueries({ queryKey: ["billing-scenarios"] });
      notifications.show({
        title: "Success",
        message: "Billing Scenario deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete billing scenario",
        color: "red",
      });
    },
  });
}

export function useToggleBillingScenarioActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      billingScenariosService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "billing-scenarios"],
      });
      queryClient.invalidateQueries({ queryKey: ["billing-scenarios"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
